import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  Bell,
  Save,
  Plus,
  Edit,
  Trash2,
  Home,
  Star,
  CheckCircle,
  AlertCircle,
  Loader,
  Crown
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Separator } from './ui/separator';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Switch } from './ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { AddressElement } from '@stripe/react-stripe-js';
import { getInitials, formatDate } from '../lib/utils';
import { Customer, Address, NotificationPreferences } from '../types';
import { toast } from 'sonner';

interface EditCustomerModalProps {
  customer: Customer | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (updatedCustomer: Customer) => Promise<void>;
}

interface CustomerFormData {
  name: string;
  email: string;
  addresses: Address[];
  notificationPreferences: NotificationPreferences;
}

export function EditCustomerModal({ customer, isOpen, onClose, onSave }: EditCustomerModalProps) {
  const [formData, setFormData] = useState<CustomerFormData | null>(null);
  const [editingAddress, setEditingAddress] = useState<Address | null>(null);
  const [showAddressForm, setShowAddressForm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Initialize form data when customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        name: customer.name || '',
        email: customer.email || '',
        addresses: customer.addresses || [],
        notificationPreferences: customer.notificationPreferences || {
          orderUpdates: true,
          promotions: false,
          newsletters: false
        }
      });
    }
  }, [customer]);

  if (!isOpen || !customer || !formData) return null;

  const rushdMembership = customer.rushdMembership;
  const isRushdMember = !!rushdMembership?.isMember;

  const formatMembershipDate = (date?: Date) => {
    if (!date) {
      return 'Not available';
    }
    return formatDate(date);
  };

  const getRushdStatusLabel = () => {
    if (!rushdMembership) {
      return 'Not a member';
    }
    if (rushdMembership.isActive) {
      return 'Active';
    }
    switch (rushdMembership.status) {
      case 'past_due':
        return 'Past Due';
      case 'canceled':
        return 'Canceled';
      case 'trialing':
        return 'Trialing';
      case 'incomplete':
        return 'Incomplete';
      default:
        return 'Inactive';
    }
  };

  const getRushdStatusBadgeClass = () => {
    if (!rushdMembership) {
      return 'bg-gray-100 text-gray-700 border-gray-200';
    }
    if (rushdMembership.isActive) {
      return 'bg-emerald-100 text-emerald-800 border-emerald-200';
    }
    switch (rushdMembership.status) {
      case 'past_due':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'canceled':
        return 'bg-gray-100 text-gray-800 border-gray-200';
      case 'trialing':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      default:
        return 'bg-amber-100 text-amber-800 border-amber-200';
    }
  };

  // Validation
  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    // Only validate non-address fields since Stripe handles address validation
    if (!formData.name?.trim()) {
      newErrors.name = 'Name is required';
    }


    console.log('Validation errors found:', newErrors);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSave = async () => {
    console.log('Validating form data:', formData);
    
    if (!validateForm()) {
      console.log('Validation failed with errors:', errors);
      toast.error('Please fix the errors before saving');
      return;
    }

    console.log('Validation passed, proceeding with save');
    setSaving(true);
    try {
      const updatedCustomer: Customer = {
        ...customer,
        name: formData.name,
        email: formData.email, // Email is read-only but we keep it
        addresses: formData.addresses,
        notificationPreferences: formData.notificationPreferences
      };

      await onSave(updatedCustomer);
      onClose();
    } catch (error) {
      console.error('Error updating customer:', error);
      toast.error('Failed to update customer. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Address management functions
  const handleAddAddress = () => {
    const newAddress: Address = {
      id: `temp_${Date.now()}`,
      label: '',
      fullName: formData.name,
      line1: '',
      line2: '',
      city: '',
      state: '',
      zipCode: '',
      country: 'US',
      phoneNumber: '',
      isDefault: formData.addresses.length === 0
    };
    setEditingAddress(newAddress);
    setShowAddressForm(true);
  };

  const handleEditAddress = (address: Address) => {
    setEditingAddress({ ...address });
    setShowAddressForm(true);
  };

  const handleSaveAddress = (address: Address) => {
    const updatedAddresses = [...formData.addresses];
    const existingIndex = updatedAddresses.findIndex(a => a.id === address.id);

    if (existingIndex >= 0) {
      // Update existing address
      updatedAddresses[existingIndex] = address;
    } else {
      // Add new address
      address.id = `addr_${Date.now()}`;
      updatedAddresses.push(address);
    }

    // If this is set as default, remove default from others
    if (address.isDefault) {
      updatedAddresses.forEach(addr => {
        if (addr.id !== address.id) {
          addr.isDefault = false;
        }
      });
    }

    setFormData({ ...formData, addresses: updatedAddresses });
    setShowAddressForm(false);
    setEditingAddress(null);
  };

  const handleDeleteAddress = (addressId: string) => {
    const updatedAddresses = formData.addresses.filter(addr => addr.id !== addressId);
    
    // If we deleted the default address, make the first remaining address default
    if (updatedAddresses.length > 0 && !updatedAddresses.some(addr => addr.isDefault)) {
      updatedAddresses[0].isDefault = true;
    }
    
    setFormData({ ...formData, addresses: updatedAddresses });
  };

  const handleSetDefaultAddress = (addressId: string) => {
    const updatedAddresses = formData.addresses.map(addr => ({
      ...addr,
      isDefault: addr.id === addressId
    }));
    setFormData({ ...formData, addresses: updatedAddresses });
  };

  return (
    <>
      <AnimatePresence>
        <div className="fixed inset-0 bg-black/60 backdrop-blur-md z-[10001] flex items-center justify-center p-4" style={{ margin: 0, padding: '1rem', top: 0, left: 0, right: 0, bottom: 0 }}>
          <motion.div
            initial={{ opacity: 0, scale: 0.9, y: 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ 
              type: "spring",
              duration: 0.5,
              bounce: 0.3 
            }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden"
          >
            {/* Enhanced Header */}
            <div className="relative bg-gradient-to-r from-emerald-600 to-teal-600 p-6 flex-shrink-0">
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-600/20 to-transparent"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ delay: 0.2, type: "spring", bounce: 0.6 }}
                    className="h-12 w-12 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-white/30"
                  >
                    <User className="h-6 w-6 text-white" />
                  </motion.div>
                  <div>
                    <motion.h2 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.1 }}
                      className="text-2xl font-bold text-white"
                    >
                      Edit Customer Profile
                    </motion.h2>
                    <motion.p 
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.2 }}
                      className="text-white/80 text-sm"
                    >
                      Update {customer.name}'s information and preferences
                    </motion.p>
                  </div>
                </div>
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                >
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={onClose}
                    className="text-white hover:bg-white/20 h-10 w-10 rounded-full"
                  >
                    <X className="h-5 w-5" />
                  </Button>
                </motion.div>
              </div>
            </div>

            {/* Enhanced Content - keeping existing functionality */}
            <div className="flex-1 overflow-y-auto p-8 bg-gradient-to-b from-gray-50/50 to-white">
              {/* Customer Info Section */}
              <motion.div 
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 }}
                className="space-y-6 mb-8"
              >
                <Label className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <User className="h-5 w-5 text-emerald-600" />
                  Basic Information
                </Label>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <Label htmlFor="name" className="text-sm font-medium text-gray-900">
                      Full Name *
                    </Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className={`h-12 rounded-xl transition-all duration-200 ${
                        errors.name 
                          ? 'border-red-300 focus:border-red-500 focus:ring-red-200' 
                          : 'border-gray-200 focus:border-emerald-600 focus:ring-emerald-600/20'
                      } shadow-sm hover:shadow-md focus:shadow-lg`}
                    />
                    {errors.name && (
                      <motion.p 
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        className="text-sm text-red-600 flex items-center gap-1"
                      >
                        <div className="w-1 h-1 bg-red-600 rounded-full"></div>
                        {errors.name}
                      </motion.p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <Label className="text-sm font-medium text-gray-900">
                      Email Address
                    </Label>
                    <div className="relative">
                      <Input
                        value={formData.email}
                        disabled
                        className="h-12 rounded-xl bg-gray-50 text-gray-500 cursor-not-allowed"
                      />
                      <p className="text-xs text-gray-500 mt-1">Email cannot be changed</p>
                    </div>
                  </div>
                </div>

                <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                        customer.isActive ? 'bg-emerald-100' : 'bg-gray-100'
                      }`}>
                        {customer.isActive ? (
                          <CheckCircle className="w-5 h-5 text-emerald-600" />
                        ) : (
                          <AlertCircle className="w-5 h-5 text-gray-600" />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Label className="text-sm font-medium text-gray-900">
                            Account Status
                          </Label>
                          <Badge className={`${
                            customer.isActive 
                              ? 'bg-emerald-100 text-emerald-800 border-emerald-200' 
                              : 'bg-gray-100 text-gray-800 border-gray-200'
                          } border rounded-lg`}>
                            {customer.isActive ? 'Active' : 'Inactive'}
                          </Badge>
                        </div>
                        <p className="text-xs text-gray-600 mt-1">
                          {customer.isActive 
                            ? 'Customer has placed an order within the past 90 days' 
                            : 'Customer has not placed an order in the past 90 days'}
                        </p>
                      </div>
                    </div>
                    <div className={`w-3 h-3 rounded-full ${
                      customer.isActive ? 'bg-emerald-500' : 'bg-gray-400'
                    }`}></div>
                  </div>
                </div>

                {isRushdMember && rushdMembership && (
                  <div className="p-5 bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl border border-amber-200 shadow-sm space-y-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-white/80 flex items-center justify-center text-amber-600">
                          <Crown className="w-5 h-5" />
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-amber-900">JamPack'd Rush'd</p>
                          <p className="text-xs text-amber-700 capitalize">
                            {rushdMembership.plan ? `${rushdMembership.plan} plan` : 'Member'}
                          </p>
                        </div>
                      </div>
                      <Badge className={`${getRushdStatusBadgeClass()} border rounded-lg`}>
                        {getRushdStatusLabel()}
                      </Badge>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm text-gray-700">
                      <div>
                        <p className="text-xs uppercase tracking-wide text-gray-500">Current Period Ends</p>
                        <p className="font-semibold text-gray-900">
                          {formatMembershipDate(rushdMembership.currentPeriodEnd)}
                        </p>
                      </div>
                    </div>

                    {typeof rushdMembership.usageLimit === 'number' && (
                      <div className="space-y-2">
                        <div className="flex items-center justify-between text-xs text-gray-600">
                          <span>Monthly usage</span>
                          <span className="font-semibold text-gray-900">
                            {rushdMembership.usageThisPeriod ?? 0}/{rushdMembership.usageLimit}
                          </span>
                        </div>
                        <div className="h-2 bg-amber-100 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-amber-500 transition-all"
                            style={{
                              width: `${Math.min(
                                100,
                                ((rushdMembership.usageThisPeriod ?? 0) / rushdMembership.usageLimit) * 100
                              )}%`
                            }}
                          />
                        </div>
                      </div>
                    )}

                    {rushdMembership.cancelAtPeriodEnd && (
                      <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded-lg px-3 py-2">
                        <AlertCircle className="w-4 h-4" />
                        Scheduled to cancel at the end of this period
                      </div>
                    )}
                  </div>
                )}
              </motion.div>

              {/* Rest of existing content with enhanced styling */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Personal Information & Preferences */}
                <div className="space-y-6">
                  {/* Notification Preferences */}
                  <Card className="admin-card">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-base">
                        <Bell className="w-4 h-4 text-jamaica-green" />
                        Notification Preferences
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <div className="space-y-3">
                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Order Updates</Label>
                            <p className="text-xs text-gray-600">Notifications about order status</p>
                          </div>
                          <Switch
                            checked={formData.notificationPreferences.orderUpdates}
                            onCheckedChange={(checked) => 
                              setFormData({
                                ...formData,
                                notificationPreferences: {
                                  ...formData.notificationPreferences,
                                  orderUpdates: checked
                                }
                              })
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Promotions</Label>
                            <p className="text-xs text-gray-600">Marketing and promotional content</p>
                          </div>
                          <Switch
                            checked={formData.notificationPreferences.promotions}
                            onCheckedChange={(checked) => 
                              setFormData({
                                ...formData,
                                notificationPreferences: {
                                  ...formData.notificationPreferences,
                                  promotions: checked
                                }
                              })
                            }
                          />
                        </div>

                        <div className="flex items-center justify-between">
                          <div>
                            <Label>Newsletters</Label>
                            <p className="text-xs text-gray-600">Monthly newsletter subscription</p>
                          </div>
                          <Switch
                            checked={formData.notificationPreferences.newsletters}
                            onCheckedChange={(checked) => 
                              setFormData({
                                ...formData,
                                notificationPreferences: {
                                  ...formData.notificationPreferences,
                                  newsletters: checked
                                }
                              })
                            }
                          />
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Right Column - Address Management */}
                <div className="space-y-6">
                  <Card className="admin-card">
                    <CardHeader>
                      <CardTitle className="flex items-center justify-between text-base">
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4 text-jamaica-green" />
                          Addresses ({formData.addresses.length})
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={handleAddAddress}
                          className="flex items-center gap-2"
                        >
                          <Plus className="w-4 h-4" />
                          Add Address
                        </Button>
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {formData.addresses.length > 0 ? (
                        <div className="space-y-4">
                          {formData.addresses.map((address, index) => (
                            <div key={address.id} className="border rounded-lg p-4">
                              <div className="flex items-start justify-between mb-2">
                                <div className="flex items-center gap-2">
                                  <Home className="w-4 h-4 text-gray-400" />
                                  <span className="font-medium text-sm">{address.label}</span>
                                  {address.isDefault && (
                                    <Badge className="bg-jamaica-green/10 text-jamaica-green border-jamaica-green/20">
                                      Primary
                                    </Badge>
                                  )}
                                </div>
                                <div className="flex items-center gap-1">
                                  {!address.isDefault && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleSetDefaultAddress(address.id)}
                                      className="h-8 w-8 p-0"
                                    >
                                      <Star className="w-3 h-3" />
                                    </Button>
                                  )}
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleEditAddress(address)}
                                    className="h-8 w-8 p-0"
                                  >
                                    <Edit className="w-3 h-3" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleDeleteAddress(address.id)}
                                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                                  >
                                    <Trash2 className="w-3 h-3" />
                                  </Button>
                                </div>
                              </div>
                              <div className="text-sm text-gray-600 space-y-1">
                                <div className="font-medium">{address.fullName}</div>
                                <div>{address.line1}</div>
                                {address.line2 && <div>{address.line2}</div>}
                                <div>{address.city}, {address.state} {address.zipCode}</div>
                                <div>{address.country}</div>
                                <div className="flex items-center gap-1 mt-2">
                                  <Phone className="w-3 h-3 text-gray-400" />
                                  <span>{address.phoneNumber}</span>
                                </div>
                              </div>
                              {errors[`address_${index}_fullName`] && (
                                <p className="text-sm text-red-600 mt-2">This address has errors that need to be fixed</p>
                              )}
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-6 text-gray-500">
                          <MapPin className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                          <p className="text-sm">No addresses added yet</p>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleAddAddress}
                            className="mt-2"
                          >
                            Add First Address
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              </div>
            </div>

            {/* Enhanced Footer */}
            <div className="p-6 bg-gray-50 border-t border-gray-100 flex-shrink-0">
              <div className="flex items-center justify-end gap-4">
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    variant="outline" 
                    onClick={onClose}
                    disabled={saving}
                    className="px-6 h-11 rounded-xl font-medium"
                  >
                    Cancel
                  </Button>
                </motion.div>
                <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
                  <Button 
                    onClick={handleSave}
                    disabled={saving}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white px-8 h-11 rounded-xl font-semibold shadow-lg shadow-emerald-600/25"
                  >
                    {saving ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        Saving Changes...
                      </>
                    ) : (
                      <>
                        <Save className="w-4 h-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                </motion.div>
              </div>
            </div>
          </motion.div>
        </div>
      </AnimatePresence>

      {/* Address Form Modal - keep existing */}
      <AddressFormModal
        address={editingAddress}
        isOpen={showAddressForm && editingAddress !== null}
        onClose={() => {
          setShowAddressForm(false);
          setEditingAddress(null);
        }}
        onSave={handleSaveAddress}
        existingAddresses={formData.addresses}
      />
    </>
  );
}

// Address Form Modal Component
interface AddressFormModalProps {
  address: Address | null;
  isOpen: boolean;
  onClose: () => void;
  onSave: (address: Address) => void;
  existingAddresses: Address[];
}

function AddressFormModal({ address, isOpen, onClose, onSave, existingAddresses }: AddressFormModalProps) {
  const [addressLabel, setAddressLabel] = useState(address?.label || '');
  const [addressDetails, setAddressDetails] = useState<any>(null);
  const [addressComplete, setAddressComplete] = useState(false);
  const [isDefaultAddress, setIsDefaultAddress] = useState(address?.isDefault || false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [resetKey, setResetKey] = useState(Date.now());

  const normalizeCountryCode = (value?: string) => {
    if (!value) return 'US';
    const trimmed = value.trim();
    if (trimmed.length === 2) return trimmed.toUpperCase();
    const normalized = trimmed.toLowerCase();
    if (normalized === 'united states' || normalized === 'united states of america' || normalized === 'usa') {
      return 'US';
    }
    return trimmed;
  };

  const isEditing = address && !address.id.startsWith('temp_');

  useEffect(() => {
    if (address) {
      setAddressLabel(address.label || '');
      setIsDefaultAddress(address.isDefault || false);
      setResetKey(Date.now()); // Force re-render of AddressElement
    }
  }, [address]);

  // Don't render if address is null
  if (!address) {
    return null;
  }

  // Handle the address element change event
  const handleAddressChange = (event: any) => {
    setAddressDetails(event);
    setAddressComplete(event.complete);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!addressLabel.trim()) {
      newErrors.label = 'Address label is required';
    }

    if (!addressComplete) {
      newErrors.address = 'Please complete all address fields';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      toast.error('Please fix the errors before saving');
      return;
    }

    setIsSubmitting(true);
    try {
      const addressValue = addressDetails.value;
      const formattedAddress: Address = {
        id: address.id,
        label: addressLabel,
        fullName: addressValue.name,
        line1: addressValue.address.line1,
        line2: addressValue.address.line2 || '',
        city: addressValue.address.city,
        state: addressValue.address.state,
        zipCode: addressValue.address.postal_code,
        country: addressValue.address.country,
        phoneNumber: addressValue.phone || '',
        isDefault: isDefaultAddress,
      };

      onSave(formattedAddress);
      toast.success(isEditing ? 'Address updated successfully' : 'Address added successfully');
    } catch (error) {
      console.error('Error saving address:', error);
      toast.error('Failed to save address. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle>
            {isEditing ? 'Edit Address' : 'Add Address'}
          </DialogTitle>
        </DialogHeader>
        
        <div className="flex-1 overflow-y-auto py-4 space-y-4">
          {/* Address Label */}
          <div>
            <Label htmlFor="address-label">Address Label *</Label>
            <Input
              id="address-label"
              placeholder="Home, Work, etc."
              value={addressLabel}
              onChange={(e) => setAddressLabel(e.target.value)}
              className={errors.label ? 'border-red-300' : ''}
            />
            {errors.label && <p className="text-sm text-red-600 mt-1">{errors.label}</p>}
          </div>

          {/* Stripe Address Element */}
          <div className="space-y-2">
            <Label>Address Details *</Label>
            <div className="border rounded-md p-3">
              {!isEditing ? (
                // Use key to force re-render for new addresses
                <AddressElement
                  key={resetKey}
                  options={{
                    mode: "shipping",
                    fields: {
                      phone: "always",
                    },
                    validation: {
                      phone: {
                        required: "always",
                      },
                    },
                    // Empty default values for a new address
                    defaultValues: {
                      name: '',
                      address: {
                        line1: '',
                        line2: '',
                        city: '',
                        state: '',
                        postal_code: '',
                        country: 'US',
                      },
                      phone: '',
                    }
                  }}
                  onChange={handleAddressChange}
                />
              ) : (
                // Normal behavior for editing
                <AddressElement
                  options={{
                    mode: "shipping",
                    fields: {
                      phone: "always",
                    },
                    validation: {
                      phone: {
                        required: "always",
                      },
                    },
                    // Prefill with current address data
                    defaultValues: address ? {
                      name: address.fullName,
                      address: {
                        line1: address.line1,
                        line2: address.line2 || '',
                        city: address.city,
                        state: address.state,
                        postal_code: address.zipCode,
                        country: normalizeCountryCode(address.country),
                      },
                      phone: address.phoneNumber || '',
                    } : undefined
                  }}
                  onChange={handleAddressChange}
                />
              )}
            </div>
            {errors.address && <p className="text-sm text-red-600 mt-1">{errors.address}</p>}
          </div>

          {/* Primary Address Toggle */}
          <div className="flex items-center space-x-2 pt-2">
            <Switch
              id="default-address"
              checked={isDefaultAddress}
              onCheckedChange={setIsDefaultAddress}
            />
            <Label htmlFor="default-address" className="text-sm">Primary Address</Label>
          </div>
        </div>

        <div className="flex justify-end gap-2 pt-4 flex-shrink-0">
          <Button variant="outline" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-jamaica-green hover:bg-jamaica-green/90"
            disabled={!addressComplete || isSubmitting}
          >
            {isSubmitting ? (
              <>
                <motion.div
                  animate={{ rotate: 360 }}
                  transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                  className="w-4 h-4 border-2 border-white border-t-transparent rounded-full mr-2"
                />
                {isEditing ? "Updating..." : "Adding..."}
              </>
            ) : (
              isEditing ? "Save Changes" : "Add Address"
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
} 
